import { HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";
import pMap from "p-map";

import { CachedObject, PublicId } from "types/misc";
import { AppObjectDB } from "utils/appObjectDb";
import { searchByPatterns } from "utils/search";

const RESOLVE_BATCH_SIZE = 50_000;
/** Concurrency for parallel resolve batches (same order of magnitude as search batch concurrency). */
const RESOLVE_PARALLEL_CONCURRENCY = 5;
/**
 * If there are fewer than this number of objects - don't use resolve.
 * Search returns more data and speed on this amount of objects won't differ noticeably.
 */
export const RESOLVE_MIN_SIZE = 10;
const SEARCH_BATCH_SIZE = 500;

const PUBLIC_ID_KEY = "Procore/InstanceId";
const PUBLIC_ID_KEY_OLD = "Procore/Id";

export const PUBLIC_ID_NULL_VALUE = "";

/**
 * Batches inputs, optionally probes the first batch when `isResolveEndpointAvailable` is
 * `undefined`, then runs remaining batches in parallel. Caller supplies the DB call and how to
 * merge each batch’s mapping.
 *
 * @returns `false` if the resolve endpoint is disabled or the probe batch fails; `true` if the
 *   endpoint is available and all batches were processed (including when `items` is empty).
 */
async function runBatchedResolve<T, R>(
    items: T[],
    db: AppObjectDB,
    abortSignal: AbortSignal,
    resolveBatch: (batch: T[], signal: AbortSignal) => Promise<R[] | undefined>,
    mergeBatch: (batch: T[], mapping: R[] | undefined) => void,
): Promise<boolean> {
    if (db.isResolveEndpointAvailable === false) {
        return false;
    }

    if (items.length === 0) {
        return true;
    }

    const allBatches: T[][] = [];
    for (let i = 0; i < items.length; i += RESOLVE_BATCH_SIZE) {
        allBatches.push(items.slice(i, i + RESOLVE_BATCH_SIZE));
    }

    if (db.isResolveEndpointAvailable === undefined) {
        // Probe the first batch to determine if the resolve endpoint is available
        const firstBatch = allBatches[0]!;
        const firstMapping = await resolveBatch(firstBatch, abortSignal);
        if (firstMapping === undefined) {
            return false;
        }
        mergeBatch(firstBatch, firstMapping);
        allBatches.shift();
    }

    // Run remaining batches in parallel
    await pMap(
        allBatches,
        async (batch) => {
            const mapping = await resolveBatch(batch, abortSignal);
            mergeBatch(batch, mapping);
        },
        { concurrency: RESOLVE_PARALLEL_CONCURRENCY },
    );

    return true;
}

/**
 * Resolve object IDs → Procore/InstanceId strings via /resolve.
 * When endpoint availability is unknown (`db.isResolveEndpointAvailable === undefined`), the first
 * batch runs sequentially as a probe; remaining batches then run in parallel. Once availability is
 * known, all batches run in parallel immediately.
 * Returns `undefined` if the resolve endpoint is not available.
 */
async function resolveObjectsByObjectId({
    objectIds,
    db,
    abortSignal,
}: {
    objectIds: ObjectId[];
    db: AppObjectDB;
    abortSignal: AbortSignal;
}): Promise<
    | undefined
    | {
          objectId2ObjectMap: Map<ObjectId, CachedObject>;
      }
> {
    if (db.isResolveEndpointAvailable === false) {
        return;
    }

    const objectId2ObjectMap = new Map<ObjectId, CachedObject>();

    if (objectIds.length === 0) {
        return { objectId2ObjectMap };
    }

    const mergeBatch = (batch: ObjectId[], mapping: (string | null)[] | undefined) => {
        if (mapping === undefined) {
            return;
        }
        for (let j = 0; j < batch.length; j++) {
            const instanceId = mapping[j];
            if (instanceId === null) {
                continue;
            }
            if (instanceId === PUBLIC_ID_NULL_VALUE) {
                continue;
            }
            const internalId = batch[j];
            const obj: CachedObject = {
                internalId,
                publicId: instanceId,
                oldPublicId: undefined,
                bbox: undefined,
            };
            objectId2ObjectMap.set(internalId, obj);
        }
    };

    const ok = await runBatchedResolve(
        objectIds,
        db,
        abortSignal,
        (batch, signal) => db.resolveObjectIdsToInstanceIds(batch, signal),
        mergeBatch,
    );
    if (!ok) {
        return undefined;
    }
    return { objectId2ObjectMap };
}

export async function collectAllObjectsByObjectId({
    objectIds,
    db,
    abortSignal,
    requireBbox = false,
}: {
    objectIds: ObjectId[];
    db: AppObjectDB;
    abortSignal: AbortSignal;
    requireBbox?: boolean;
}): Promise<{ objectId2ObjectMap: Map<ObjectId, CachedObject> }> {
    if (objectIds.length === 0) {
        return { objectId2ObjectMap: new Map() };
    }

    if (!requireBbox && objectIds.length >= RESOLVE_MIN_SIZE) {
        const resolved = await resolveObjectsByObjectId({ objectIds, db, abortSignal });
        if (resolved) {
            return resolved;
        }
    }

    const objectId2ObjectMap = new Map<ObjectId, CachedObject>();
    const searchBatches: string[][] = [];
    for (let i = 0; i < objectIds.length; i += SEARCH_BATCH_SIZE) {
        searchBatches.push(objectIds.slice(i, i + SEARCH_BATCH_SIZE).map(String));
    }
    await pMap(
        searchBatches,
        async (batch) => {
            const refs: HierarcicalObjectReference[] = [];
            await searchByPatterns({
                db,
                abortSignal,
                callback: (foundRefs) => refs.push(...foundRefs),
                full: true,
                searchPatterns: [{ property: "id", value: batch, exact: true }],
            }).catch((_) => {
                // Swallow errors to continue processing other batches
            });
            getCachedObjectsByObjectId(refs, objectId2ObjectMap);
        },
        { concurrency: 5 },
    );

    return { objectId2ObjectMap };
}

function getCachedObjectsByObjectId(
    refs: HierarcicalObjectReference[],
    map: Map<ObjectId, CachedObject>,
): CachedObject[] {
    const objects: CachedObject[] = refs.map((ref): CachedObject => {
        const objectData = ref as ObjectData;
        const publicId = getPublicId(objectData);
        const oldPublicId = getOldPublicId(objectData);
        const obj: CachedObject = {
            internalId: ref.id,
            publicId,
            oldPublicId,
            bbox: ref.bounds?.box ?? null, // set to null to distinguish from undefined (not loaded)
        };
        map.set(ref.id, obj);
        return obj;
    });
    return objects.filter((obj) => obj.publicId !== PUBLIC_ID_NULL_VALUE);
}

export function getPublicId(obj: ObjectData): PublicId {
    const publicId = obj.properties.find((prop) => prop[0] === PUBLIC_ID_KEY);
    return publicId ? publicId[1] : PUBLIC_ID_NULL_VALUE;
}

export function getOldPublicId(obj: ObjectData): PublicId {
    const publicId = obj.properties.find((prop) => prop[0] === PUBLIC_ID_KEY_OLD);
    return publicId ? publicId[1] : PUBLIC_ID_NULL_VALUE;
}

/**
 * Resolve Procore/InstanceId strings → object IDs via /resolve.
 * When endpoint availability is unknown (`db.isResolveEndpointAvailable === undefined`), the first
 * batch runs sequentially as a probe; remaining batches then run in parallel. Once availability is
 * known, all batches run in parallel immediately.
 * Returns `undefined` if the resolve endpoint is not available.
 */
async function resolveObjectsByPublicId({
    publicIds,
    db,
    abortSignal,
}: {
    publicIds: PublicId[];
    db: AppObjectDB;
    abortSignal: AbortSignal;
}): Promise<undefined | { publicId2ObjectMap: Map<PublicId, CachedObject> }> {
    if (db.isResolveEndpointAvailable === false) {
        return;
    }

    const publicId2ObjectMap = new Map<PublicId, CachedObject>();

    if (publicIds.length === 0) {
        return { publicId2ObjectMap };
    }

    const mergeBatch = (batch: PublicId[], mapping: (ObjectId | null)[] | undefined) => {
        if (mapping === undefined) {
            return;
        }
        for (let j = 0; j < batch.length; j++) {
            const internalId = mapping[j];
            if (internalId === null) {
                continue;
            }
            const publicId = batch[j];
            if (publicId === PUBLIC_ID_NULL_VALUE) {
                continue;
            }
            const obj: CachedObject = {
                internalId,
                publicId,
                oldPublicId: undefined,
                bbox: undefined,
            };
            publicId2ObjectMap.set(publicId, obj);
        }
    };

    const ok = await runBatchedResolve(
        publicIds,
        db,
        abortSignal,
        (batch, signal) => db.resolveInstanceIdsToObjectIds(batch, signal),
        mergeBatch,
    );
    if (!ok) {
        return undefined;
    }
    return { publicId2ObjectMap };
}

export async function collectAllObjectsByPublicId({
    publicIds,
    db,
    useOldPublicId,
    abortSignal,
}: {
    publicIds: PublicId[];
    db: AppObjectDB;
    useOldPublicId: boolean;
    abortSignal: AbortSignal;
}): Promise<{ publicId2ObjectMap: Map<PublicId, CachedObject> }> {
    if (publicIds.length === 0) {
        return { publicId2ObjectMap: new Map() };
    }

    if (!useOldPublicId && publicIds.length >= RESOLVE_MIN_SIZE) {
        const resolved = await resolveObjectsByPublicId({ publicIds, db, abortSignal });
        if (resolved) {
            return resolved;
        }
    }

    const publicId2ObjectMap = new Map<PublicId, CachedObject>();
    await collectPublicIdsBySearch({ publicIds, db, publicId2ObjectMap, useOldPublicId, abortSignal });
    return { publicId2ObjectMap };
}

async function collectPublicIdsBySearch({
    publicIds,
    db,
    publicId2ObjectMap,
    useOldPublicId,
    abortSignal,
}: {
    publicIds: PublicId[];
    db: AppObjectDB;
    publicId2ObjectMap: Map<PublicId, CachedObject>;
    useOldPublicId: boolean;
    abortSignal: AbortSignal;
}): Promise<void> {
    const batches: string[][] = [];
    for (let i = 0; i < publicIds.length; i += SEARCH_BATCH_SIZE) {
        batches.push(publicIds.slice(i, i + SEARCH_BATCH_SIZE));
    }
    const concurrentRequests = 5;
    await pMap(
        batches,
        async (batch) => {
            const refs: HierarcicalObjectReference[] = [];
            await searchByPatterns({
                db,
                abortSignal,
                callback: (foundRefs) => refs.push(...foundRefs),
                full: true,
                searchPatterns: [
                    { property: useOldPublicId ? PUBLIC_ID_KEY_OLD : PUBLIC_ID_KEY, value: batch, exact: true },
                ],
            }).catch((_) => {
                // Swallow errors to continue processing other batches
            });
            getCachedObjectsByPublicId(refs, publicId2ObjectMap, useOldPublicId);
        },
        { concurrency: concurrentRequests },
    );
}

function getCachedObjectsByPublicId(
    refs: HierarcicalObjectReference[],
    map: Map<PublicId, CachedObject>,
    useOldPublicId: boolean,
) {
    refs.forEach((ref) => {
        const objectData = ref as ObjectData;
        const publicId = getPublicId(objectData);
        const oldPublicId = getOldPublicId(objectData);
        const id = useOldPublicId ? oldPublicId : publicId;
        if (id !== PUBLIC_ID_NULL_VALUE) {
            const obj: CachedObject = {
                internalId: ref.id,
                publicId,
                oldPublicId,
                bbox: ref.bounds?.box ?? null, // set to null to distinguish from undefined (not loaded)
            };
            map.set(id, obj);
        }
    });
}
